export type DownloadPhase = 'fetching' | 'downloading' | 'creating' | 'complete'

interface DownloadProgressModalProps {
  isOpen: boolean
  phase: DownloadPhase
  current: number
  total: number
  onCancel: () => void
}

const phaseLabels: Record<DownloadPhase, string> = {
  fetching: 'Fetching details',
  downloading: 'Downloading images',
  creating: 'Creating ZIP',
  complete: 'Complete',
}

const phaseIcons: Record<DownloadPhase, string> = {
  fetching: 'description',
  downloading: 'cloud_download',
  creating: 'folder_zip',
  complete: 'check_circle',
}

export function DownloadProgressModal({
  isOpen,
  phase,
  current,
  total,
  onCancel,
}: DownloadProgressModalProps) {
  if (!isOpen) return null

  const progress = total > 0 ? (current / total) * 100 : 0
  const isComplete = phase === 'complete'

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 lightbox-enter">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 lightbox-image-enter">
        {/* Phase indicator */}
        <div className="flex items-center justify-center gap-3 mb-6">
          {(['fetching', 'downloading', 'creating'] as const).map((p, i) => (
            <div key={p} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  phase === p
                    ? 'bg-primary text-white'
                    : phase === 'complete' ||
                      (['fetching', 'downloading', 'creating'].indexOf(phase) > i)
                    ? 'bg-primary/20 text-primary'
                    : 'bg-surface-muted text-text-light'
                }`}
              >
                {phase === 'complete' ||
                (['fetching', 'downloading', 'creating'].indexOf(phase) > i) ? (
                  <span className="material-symbols-outlined text-sm">check</span>
                ) : (
                  <span className="material-symbols-outlined text-sm">{phaseIcons[p]}</span>
                )}
              </div>
              {i < 2 && (
                <div
                  className={`w-6 h-0.5 mx-1 transition-colors ${
                    (['fetching', 'downloading', 'creating'].indexOf(phase) > i) || phase === 'complete'
                      ? 'bg-primary/30'
                      : 'bg-surface-muted'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Current phase label */}
        <h3 className="text-lg font-semibold text-text-main text-center mb-2">
          {isComplete ? 'Download Complete!' : phaseLabels[phase]}
        </h3>

        {/* Progress info */}
        {!isComplete && (
          <p className="text-sm text-text-muted text-center mb-4">
            {phase === 'fetching' && `Fetching ${current} of ${total} generations...`}
            {phase === 'downloading' && `Downloading ${current} of ${total} images...`}
            {phase === 'creating' && 'Creating your ZIP file...'}
          </p>
        )}

        {/* Progress bar */}
        {!isComplete && (
          <div className="mb-6">
            <div className="h-2 bg-surface-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-300 ease-out rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Action */}
        {isComplete ? (
          <button
            onClick={onCancel}
            className="w-full px-4 py-2.5 rounded-xl bg-primary text-white font-medium hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined text-sm">check</span>
            Done
          </button>
        ) : (
          <button
            onClick={onCancel}
            className="w-full px-4 py-2.5 rounded-xl border border-border-color text-text-main font-medium hover:bg-surface-muted transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  )
}
